import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  Activity, Building2, ChevronDown, Database, Columns3, FileCheck, FileText, HelpCircle, KeyRound,
  FolderOpen, Landmark, LayoutGrid, LifeBuoy, ListTodo, Mail, Menu, Rocket, ShieldCheck, Store, ToggleLeft, Upload, Users,
  Wrench, X,
} from "lucide-react";

/** Pages kept directly in the bar: the ones opened every day. */
const DAILY = [
  { to: "/admin", label: "Overview", icon: LayoutGrid, tid: "nav-admin-overview" },
  { to: "/admin/users", label: "Users", icon: Users, tid: "nav-admin-users" },
  { flag: "page.admin_companies", to: "/admin/companies", label: "Employers", icon: Building2, tid: "nav-admin-companies" },
];

/** Groups named for the work, not the data. */
const GROUPS = [
  {
    name: "Program", tid: "nav-group-program", items: [
      { flag: "page.admin_compare", to: "/admin/compare", label: "Compare", icon: Columns3, tid: "nav-admin-compare" },
      { flag: "page.admin_setup", to: "/admin/setup", label: "Settings", icon: Activity, tid: "nav-admin-setup" },
      { flag: "page.admin_attestations", to: "/admin/attestations", label: "Attestations", icon: FileCheck, tid: "nav-admin-attestations" },
    ],
  },
  {
    name: "Operations", tid: "nav-group-operations", items: [
      { flag: "page.admin_invoices", to: "/admin/invoices", label: "Invoices", icon: FileText, tid: "nav-admin-invoices" },
      { flag: "page.admin_carriers", to: "/admin/carriers", label: "Carriers", icon: Landmark, tid: "nav-admin-carriers" },
      { flag: "page.admin_vendors", to: "/admin/vendors", label: "Vendors", icon: Store, tid: "nav-admin-vendors" },
      { to: "/files", label: "Files", icon: FolderOpen, tid: "nav-admin-files" },
      { flag: "page.admin_emails", to: "/admin/emails", label: "Emails", icon: Mail, tid: "nav-admin-emails" },
    ],
  },
  {
    name: "System", tid: "nav-group-system", items: [
      { flag: "page.admin_audit", to: "/admin/audit", label: "Audit", icon: ShieldCheck, tid: "nav-admin-audit" },
      { flag: "page.admin_security", to: "/admin/security", label: "Security", icon: KeyRound, tid: "nav-admin-security" },
      { to: "/admin/sso", label: "SSO & Emergency Access", icon: LifeBuoy, tid: "nav-admin-sso", perm: "system" },
      { flag: "page.admin_backup", to: "/admin/backup", label: "Backup", icon: Database, tid: "nav-admin-backup" },
      { flag: "page.admin_migration", to: "/admin/migration", label: "Migration", icon: Upload, tid: "nav-admin-migration" },
      { flag: "page.admin_backlog", to: "/admin/backlog", label: "Backlog", icon: ListTodo, tid: "nav-admin-backlog" },
      { to: "/admin/maintenance", label: "Maintenance", icon: Wrench, tid: "nav-admin-maintenance" },
      { to: "/admin/release", label: "Release", icon: ToggleLeft, tid: "nav-admin-release" },
      { to: "/admin/launch", label: "Cut-over", icon: Rocket, tid: "nav-admin-launch", perm: "system" },
    ],
  },
];

const ADMIN_HELP = { flag: "page.help", to: "/help", label: "Help", icon: HelpCircle, tid: "nav-help" };

const isActive = (pathname, to) =>
  pathname === to || (to !== "/admin" && pathname.startsWith(to));

const barItemStyle = (active) => ({
  color: active ? "var(--ink)" : "var(--muted)",
  borderBottom: active ? "2px solid var(--ink)" : "2px solid transparent",
});

export function AdminNav({ isOn }) {
  const loc = useLocation();
  const [open, setOpen] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef(null);

  // Nothing is remembered: every page view starts with all groups closed.
  useEffect(() => { setOpen(""); setMobileOpen(false); }, [loc.pathname]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") { setOpen(""); setMobileOpen(false); }
    };
    const onClick = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) setOpen("");
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  const { user } = useAuth();
  const perms = user?.permissions || [];
  const allow = (item) => (!item.flag || isOn(item.flag)) && (!item.perm || perms.includes(item.perm));
  const daily = DAILY.filter(allow);
  const groups = GROUPS
    .map((group) => ({ ...group, items: group.items.filter(allow) }))
    .filter((group) => group.items.length);
  const adminHelp = allow(ADMIN_HELP) ? ADMIN_HELP : null;

  const moveFocus = (event, group, index, direction) => {
    const items = Array.from(
      document.querySelectorAll(`[data-group-panel="${group.tid}"] a`));
    if (!items.length) return;
    event.preventDefault();
    const next = direction === "home" ? 0
      : direction === "end" ? items.length - 1
      : (index + (direction === "down" ? 1 : -1) + items.length) % items.length;
    items[next].focus();
  };

  const onButtonKey = (event, group) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(group.tid);
      requestAnimationFrame(() => {
        document.querySelector(`[data-group-panel="${group.tid}"] a`)?.focus();
      });
    }
  };

  return (
    <nav className="border-t" style={{ borderColor: "var(--border)" }} data-testid="secondary-nav" ref={navRef}>
      {/* Wide screens: daily pages + grouped dropdowns */}
      <div className="hidden lg:flex items-stretch px-6 lg:px-10 mx-auto max-w-[1400px]" data-testid="admin-nav-bar">
        {daily.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.tid}
              to={item.to}
              data-testid={item.tid}
              className="px-4 py-3 text-xs font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors duration-150"
              style={barItemStyle(isActive(loc.pathname, item.to))}
            >
              <Icon size={13} /> {item.label}
            </Link>
          );
        })}

        <span className="mx-2 my-3 w-px" style={{ background: "var(--border)" }} aria-hidden="true" data-testid="admin-nav-divider" />

        {groups.map((group) => {
          const groupActive = group.items.some((item) => isActive(loc.pathname, item.to));
          const isOpen = open === group.tid;
          return (
            <div key={group.tid} className="relative">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? "" : group.tid)}
                onKeyDown={(event) => onButtonKey(event, group)}
                aria-expanded={isOpen}
                aria-haspopup="true"
                data-testid={group.tid}
                className="h-full px-4 py-3 text-xs font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors duration-150"
                style={barItemStyle(groupActive)}
              >
                {group.name}
                <ChevronDown size={12} className="transition-transform duration-150"
                             style={{ transform: isOpen ? "rotate(180deg)" : "none" }} />
              </button>
              {isOpen && (
                <div
                  className="absolute left-0 top-full z-50 min-w-[220px] border animate-in fade-in duration-150"
                  style={{ background: "var(--surface)", borderColor: "var(--border-strong)" }}
                  data-group-panel={group.tid}
                  data-testid={`${group.tid}-panel`}
                  role="menu"
                >
                  {group.items.map((item, index) => {
                    const Icon = item.icon;
                    const active = isActive(loc.pathname, item.to);
                    return (
                      <Link
                        key={item.tid}
                        to={item.to}
                        role="menuitem"
                        data-testid={item.tid}
                        onClick={() => setOpen("")}
                        onKeyDown={(event) => {
                          if (event.key === "ArrowDown") moveFocus(event, group, index, "down");
                          if (event.key === "ArrowUp") moveFocus(event, group, index, "up");
                          if (event.key === "Home") moveFocus(event, group, index, "home");
                          if (event.key === "End") moveFocus(event, group, index, "end");
                        }}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm transition-colors duration-150 hover:opacity-70"
                        style={{
                          color: active ? "var(--ink)" : "var(--muted)",
                          fontWeight: active ? 600 : 400,
                          borderLeft: active ? "2px solid var(--ink)" : "2px solid transparent",
                        }}
                      >
                        <Icon size={13} /> {item.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {adminHelp && (
          <Link
            to={adminHelp.to}
            data-testid={adminHelp.tid}
            className="px-4 py-3 text-xs font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors duration-150"
            style={barItemStyle(isActive(loc.pathname, adminHelp.to))}
          >
            <HelpCircle size={13} /> {adminHelp.label}
          </Link>
        )}
      </div>

      {/* Narrow screens: one menu button, every group stacked and visible at once */}
      <div className="lg:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-expanded={mobileOpen}
          className="w-full px-6 py-3 flex items-center justify-between text-xs font-medium"
          style={{ color: "var(--ink)" }}
          data-testid="admin-nav-mobile-toggle"
        >
          <span className="flex items-center gap-2">
            {mobileOpen ? <X size={14} /> : <Menu size={14} />} Admin menu
          </span>
          <ChevronDown size={12} style={{ transform: mobileOpen ? "rotate(180deg)" : "none" }} />
        </button>
        {mobileOpen && (
          <div className="border-t px-6 pb-5" style={{ borderColor: "var(--border)" }} data-testid="admin-nav-mobile-panel">
            <div className="py-3 grid grid-cols-2 gap-2">
              {daily.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={`m-${item.tid}`}
                    to={item.to}
                    data-testid={`${item.tid}-mobile`}
                    className="flex items-center gap-2 border px-3 py-2 text-sm"
                    style={{
                      borderColor: "var(--border-strong)",
                      color: isActive(loc.pathname, item.to) ? "var(--ink)" : "var(--muted)",
                    }}
                  >
                    <Icon size={13} /> {item.label}
                  </Link>
                );
              })}
            </div>
            {groups.map((group) => (
              <div key={`m-${group.tid}`} className="pt-3" data-testid={`${group.tid}-mobile`}>
                <div className="eyebrow text-[9px] mb-1">{group.name}</div>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={`m-${item.tid}`}
                      to={item.to}
                      data-testid={`${item.tid}-mobile`}
                      className="flex items-center gap-2 py-2 text-sm"
                      style={{ color: isActive(loc.pathname, item.to) ? "var(--ink)" : "var(--muted)" }}
                    >
                      <Icon size={13} /> {item.label}
                    </Link>
                  );
                })}
              </div>
            ))}
            {adminHelp && (
              <div className="pt-3" data-testid="admin-help-mobile-section">
                <div className="eyebrow text-[9px] mb-1">Help</div>
                <Link
                  to={adminHelp.to}
                  data-testid={`${adminHelp.tid}-mobile`}
                  className="flex items-center gap-2 py-2 text-sm"
                  style={{ color: isActive(loc.pathname, adminHelp.to) ? "var(--ink)" : "var(--muted)" }}
                >
                  <HelpCircle size={13} /> {adminHelp.label}
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
